import React, {useState, useEffect, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {Usuario} from '../core/models'
import {fetchWithAuth} from '../../../core/utils/fetchWithAuth'
import {KTIcon} from '../../../../_metronic/helpers'
import '../../socios/socios.scss'

interface FiltrosUsuarios {
  q?: string
  activo?: number
  es_admin?: number
}

interface Props {
  usuarios: Usuario[]
  onRefresh: () => void
  filtros: FiltrosUsuarios
  onFiltrosChange: (filtros: FiltrosUsuarios) => void
}

export const UsuariosTable: React.FC<Props> = ({usuarios, onRefresh, filtros, onFiltrosChange}) => {
  const navigate = useNavigate()
  
  // Estados locales para los controles de filtro
  const [searchText, setSearchText] = useState<string>(filtros.q || '')
  const [filtroActivo, setFiltroActivo] = useState<number | undefined>(filtros.activo)
  const [filtroAdmin, setFiltroAdmin] = useState<number | undefined>(filtros.es_admin)
  const [loading, setLoading] = useState<number | null>(null)
  
  // Índice de la fila que está expandida (o null si ninguna)
  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  // Asegurar que usuarios sea siempre un array
  const usuariosArray = Array.isArray(usuarios) ? usuarios : []

  // Ref para evitar loops infinitos
  const isInternalUpdate = useRef(false)
  const lastFiltrosRef = useRef<string>(JSON.stringify(filtros))

  // Sincronizar estados locales con props cuando cambien los filtros externos (solo si vienen del padre)
  useEffect(() => {
    const filtrosKey = JSON.stringify(filtros)
    
    // Solo actualizar si los filtros cambiaron desde el padre (no desde este componente)
    if (lastFiltrosRef.current !== filtrosKey && !isInternalUpdate.current) {
      setSearchText(filtros.q || '')
      setFiltroActivo(filtros.activo)
      setFiltroAdmin(filtros.es_admin)
      lastFiltrosRef.current = filtrosKey
    }
    isInternalUpdate.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.q, filtros.activo, filtros.es_admin])

  // Aplicar filtros con debounce para el campo de búsqueda
  useEffect(() => {
    // Evitar llamar onFiltrosChange si los valores son iguales a los filtros actuales
    const nuevosFiltros = {
      q: searchText || undefined,
      activo: filtroActivo,
      es_admin: filtroAdmin
    }
    
    const nuevosFiltrosKey = JSON.stringify(nuevosFiltros)
    
    // Comparar si los filtros realmente cambiaron
    if (lastFiltrosRef.current === nuevosFiltrosKey) {
      return
    }

    const timer = setTimeout(() => {
      isInternalUpdate.current = true
      onFiltrosChange(nuevosFiltros)
      lastFiltrosRef.current = nuevosFiltrosKey
    }, 500) // Debounce de 500ms

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, filtroActivo, filtroAdmin])

  // Expansión de fila
  const toggleExpandRow = (index: number) => {
    if (expandedRow === index) {
      setExpandedRow(null)
    } else {
      setExpandedRow(index)
    }
  }

  // Función para activar/desactivar usuario
  const handleToggleActivo = async (usuario: Usuario, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      setLoading(usuario.id)
      const token = localStorage.getItem('X-TOKEN')
      if (!token) {
        throw new Error('No se encontró el token de autenticación')
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token
        },
        body: JSON.stringify({
          service: 'setUsuarioActivo',
          id: usuario.id,
          activo: usuario.activo === 1 ? 0 : 1
        })
      })

      if (!response.ok) {
        throw new Error(`Error en la petición: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.code === 'OK') {
        onRefresh()
      } else {
        throw new Error(data.returned_message || 'Error al cambiar el estado del usuario')
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(null)
    }
  }

  return (
    <>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <div className='d-flex flex-wrap gap-3 align-items-center'>
            <div className='d-flex align-items-center position-relative my-1'>
              <i className='ki-duotone ki-magnifier fs-1 position-absolute ms-6'>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
              <input
                type='text'
                data-kt-user-table-filter='search'
                className='form-control form-control-solid w-250px ps-14'
                placeholder='Buscar por email/nombre/apellido...'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className='d-flex align-items-center'>
              <label className='form-label me-2 mb-0'>Estado:</label>
              <select
                className='form-select form-select-solid w-150px'
                value={filtroActivo === undefined ? '' : filtroActivo.toString()}
                onChange={(e) => setFiltroActivo(e.target.value === '' ? undefined : parseInt(e.target.value))}
              >
                <option value=''>Todos</option>
                <option value='1'>Activo</option>
                <option value='0'>Inactivo</option>
              </select>
            </div>
            <div className='d-flex align-items-center'>
              <label className='form-label me-2 mb-0'>Admin:</label>
              <select
                className='form-select form-select-solid w-150px'
                value={filtroAdmin === undefined ? '' : filtroAdmin.toString()}
                onChange={(e) => setFiltroAdmin(e.target.value === '' ? undefined : parseInt(e.target.value))}
              >
                <option value=''>Todos</option>
                <option value='1'>Sí</option>
                <option value='0'>No</option>
              </select>
            </div>
          </div>
        </div>
        <div className='card-toolbar'>
          <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
            <button 
              type='button' 
              className='btn btn-primary' 
              onClick={() => navigate('/usuarios/crear')}
            >
              <i className='ki-duotone ki-plus fs-2'></i>
              Nuevo Usuario
            </button>
          </div>
        </div>
      </div>

      <div className='card-body py-4'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-striped table-hover align-middle gs-0 gy-4'>
            <thead>
              <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                <th className='min-w-50px'>Id</th>
                <th className='min-w-150px'>Nombre</th>
                <th className='min-w-150px'>Email</th>
                <th className='min-w-100px'>Admin</th>
                <th className='min-w-100px'>Estado</th>
                <th className='min-w-150px'>Supervisor</th>
                <th className='text-end min-w-150px'>Acciones</th>
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-semibold'>
              {usuariosArray.map((usuario, index) => {
                const isExpanded = expandedRow === index

                return (
                  <React.Fragment key={usuario.id}>
                    {/* Fila principal */}
                    <tr
                      style={{cursor: 'pointer'}}
                      onClick={() => toggleExpandRow(index)}
                    >
                      <td>{usuario.id}</td>
                      <td>
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 mb-1'>{usuario.nombre} {usuario.apellido}</span>
                        </div>
                      </td>
                      <td>{usuario.email}</td>
                      <td>
                        {usuario.es_admin === 1 ? (
                          <span className='badge badge-light-warning'>Sí</span>
                        ) : (
                          <span className='badge badge-light-secondary'>No</span>
                        )}
                      </td>
                      <td>
                        {usuario.activo === 1 ? (
                          <span className='badge badge-light-success'>Activo</span>
                        ) : (
                          <span className='badge badge-light-danger'>Inactivo</span>
                        )}
                      </td>
                      <td>
                        {usuario.supervisor ? (
                          <span className='text-gray-800'>{usuario.supervisor.nombre} {usuario.supervisor.apellido}</span>
                        ) : (
                          <span className='text-muted'>Sin supervisor</span>
                        )}
                      </td>
                      <td className='text-end' onClick={(e) => e.stopPropagation()}>
                        <div className='d-flex gap-2 justify-content-end'>
                          <button
                            className='btn btn-light btn-active-light-primary btn-sm'
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              navigate(`/usuarios/editar/${usuario.id}`, { state: { usuario } })
                            }}
                          >
                            Editar
                          </button>
                          <button
                            className={`btn btn-sm ${usuario.activo === 1 ? 'btn-light-danger' : 'btn-light-success'}`}
                            onClick={(e) => handleToggleActivo(usuario, e)}
                            disabled={loading === usuario.id}
                            title={usuario.activo === 1 ? 'Desactivar' : 'Activar'}
                          >
                            {loading === usuario.id ? (
                              <span className='spinner-border spinner-border-sm' role='status'></span>
                            ) : (
                              <KTIcon iconName={usuario.activo === 1 ? 'cross' : 'check'} className='fs-5' />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Fila expandida con información adicional */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className='bg-light-primary'>
                          <div className='d-flex flex-column p-4'>
                            <div className='row mb-3'>
                              <div className='col-md-6'>
                                <span className='fw-bold text-gray-800 mb-2 d-block'>Roles:</span>
                                {usuario.roles && usuario.roles.length > 0 ? (
                                  <div className='d-flex flex-wrap gap-2'>
                                    {usuario.roles.map((rol) => (
                                      <span key={rol.id} className='badge badge-light-info'>
                                        {rol.nombre}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className='text-muted'>Sin roles asignados</span>
                                )}
                              </div>
                              <div className='col-md-6'>
                                <span className='fw-bold text-gray-800 mb-2 d-block'>Funcionalidades:</span>
                                {usuario.funcionalidades && usuario.funcionalidades.length > 0 ? (
                                  <div className='d-flex flex-wrap gap-2'>
                                    {usuario.funcionalidades.map((func) => (
                                      <span key={func.id} className='badge badge-light-primary'>
                                        {func.nombre}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className='text-muted'>Sin funcionalidades asignadas</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
              
              {usuariosArray.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                      No se encontraron registros coincidentes
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

